import React, { createContext, useContext, useEffect, useState } from 'react';

interface TempMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: Date;
  model?: string;
  selectedAgent?: any;
  attachedMedia?: any;
  isImage?: boolean;
  agentData?: any;
  searchResults?: any;
}

interface TempChatContextType {
  isTempMode: boolean;
  setTempMode: (enabled: boolean) => void;
  tempMessages: TempMessage[];
  addTempMessage: (message: Omit<TempMessage, 'id' | 'timestamp'>) => void;
  clearTempMessages: () => void;
  getTempMessagesCount: () => number;
  showIncognitoModal: boolean;
  setShowIncognitoModal: (show: boolean) => void;
  startIncognitoSession: () => void;
}

const TempChatContext = createContext<TempChatContextType | undefined>(undefined);

interface TempChatProviderProps {
  children: React.ReactNode;
}

export function TempChatProvider({ children }: TempChatProviderProps) {
  const [isTempMode, setIsTempMode] = useState(false);
  const [tempMessages, setTempMessages] = useState<TempMessage[]>([]);
  const [showIncognitoModal, setShowIncognitoModal] = useState(false);

  // Clear temp messages when mode is disabled or on page refresh
  useEffect(() => {
    if (!isTempMode) {
      setTempMessages([]);
    }
  }, [isTempMode]);

  // Clear temp messages on page refresh/reload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Messages are only stored in component state, so they'll be cleared on refresh
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const setTempMode = (enabled: boolean) => {
    setIsTempMode(enabled);
    if (!enabled) {
      setTempMessages([]);
    }
  };

  const addTempMessage = (message: Omit<TempMessage, 'id' | 'timestamp'>) => {
    const newMessage: TempMessage = {
      ...message,
      id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };
    
    setTempMessages(prev => [...prev, newMessage]);
  };

  const clearTempMessages = () => {
    setTempMessages([]);
  };

  const getTempMessagesCount = () => {
    return tempMessages.length;
  };

  const startIncognitoSession = () => {
    setIsTempMode(true);
    setTempMessages([]);
    setShowIncognitoModal(true);
  };

  const value: TempChatContextType = {
    isTempMode,
    setTempMode,
    tempMessages,
    addTempMessage,
    clearTempMessages,
    getTempMessagesCount,
    showIncognitoModal,
    setShowIncognitoModal,
    startIncognitoSession
  };

  return (
    <TempChatContext.Provider value={value}>
      {children}
    </TempChatContext.Provider>
  );
}

export const useTempChat = (): TempChatContextType => {
  const context = useContext(TempChatContext);
  if (context === undefined) {
    throw new Error('useTempChat must be used within a TempChatProvider');
  }
  return context;
};
